import { useState, useEffect } from "react";
import { Star, MessageSquare, ChevronDown, ChevronUp, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primaryDark: "#08263e",
  secondaryBlue: "#518dca",
};

const RatingBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-6 text-right font-medium text-[#666]">{label}</span>
      <Star size={12} className="text-yellow-400 fill-yellow-400 flex-shrink-0" />
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="w-8 text-right text-[#999]">{count}</span>
    </div>
  );
};

const StarDisplay = ({ rating, size = 14 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={size} className={s <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"} />
    ))}
  </div>
);

const formatTime = (ts) => {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ts; }
};

export const SurveyPanel = ({ token, surveyOverview }) => {
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/surveys?limit=50`, { headers });
      setSurveys(res.data.surveys);
      setStats(res.data.stats);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSurveys(); }, []);

  const dist = stats?.distribution || {};
  const avgRating = stats?.average_rating || surveyOverview?.average_rating || 0;
  const totalResponses = stats?.total_responses || surveyOverview?.total_responses || 0;
  const barColors = { 5: "#22c55e", 4: "#84cc16", 3: "#eab308", 2: "#f97316", 1: "#ef4444" };

  return (
    <Card className="mb-6" data-testid="survey-panel">
      <CardHeader className="flex flex-row items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 size={18} className="text-[#518dca]" /> User Satisfaction
          {totalResponses > 0 && (
            <span className="text-xs font-normal bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full ml-1">
              {avgRating} avg / {totalResponses} responses
            </span>
          )}
        </CardTitle>
        <Button variant="ghost" size="sm" data-testid="survey-toggle">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent>
          {loading ? (
            <div className="text-center py-4 text-sm text-[#999]">Loading...</div>
          ) : totalResponses === 0 ? (
            <div className="text-center py-6 text-sm text-[#999]">No survey responses yet</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Rating Distribution */}
              <div>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold" style={{ color: COLORS.primaryDark }}>{avgRating}</div>
                  <StarDisplay rating={Math.round(avgRating)} size={18} />
                  <p className="text-xs text-[#999] mt-1">{totalResponses} total responses</p>
                </div>
                <div className="space-y-1.5">
                  {[5, 4, 3, 2, 1].map((r) => (
                    <RatingBar key={r} label={r} count={dist[r] || 0} total={totalResponses} color={barColors[r]} />
                  ))}
                </div>
              </div>

              {/* Right: Recent Feedback */}
              <div className="lg:col-span-2">
                <h4 className="text-sm font-medium text-[#666] mb-2 flex items-center gap-1">
                  <MessageSquare size={14} /> Recent Feedback
                </h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {surveys.map((s, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2.5 bg-gray-50 rounded-lg" data-testid={`survey-entry-${idx}`}>
                      <div className="flex-shrink-0 mt-0.5">
                        <StarDisplay rating={s.rating} size={12} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-[#333]">{s.user_name || "Anonymous"}</span>
                          <span className="text-[#bbb]">{formatTime(s.timestamp)}</span>
                          <span className="text-[#ccc] ml-auto">{s.message_count} msgs</span>
                        </div>
                        {s.comment && <p className="text-xs text-[#666] mt-1 line-clamp-2">{s.comment}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                {total > 50 && (
                  <p className="text-xs text-center text-[#999] mt-2">Showing latest 50 of {total}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
